import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { Box, Button, Header, Text, DropButton, Nav, ResponsiveContext, Tip } from 'grommet';
import { Hpe as HpeIcon, Menu as MenuIcon, FormDown } from 'grommet-icons';
import { useHeaderHeight } from '../utils/HeaderHeightContext';
import MenuItems from '../data/header/MenuItems.json';

// Helper function to dynamically import icons from the Grommet library
const getIconComponent = (iconName) => {
  try {
    return require('grommet-icons')[iconName];
  } catch (e) {
    console.error(`Icon ${iconName} not found`, e);
    return null;  // Fallback if icon is not found
  }
};

// NavButton component to handle button rendering logic
const NavButton = ({ item, onClick, isActive, size }) => {
  const IconComponent = item.icon ? React.createElement(getIconComponent(item.icon), { color: item.color || 'text-strong' }) : null;
  const buttonProps = {
    onClick: () => onClick(item),
    active: isActive(item.path),
    pad: { vertical: 'xsmall', horizontal: 'small' },
    style: { whiteSpace: 'nowrap', verticalAlign: 'middle' },
  };

  // Adjust vertical alignment for icon buttons on desktop
  const buttonAlignment = item.icon && size !== 'small' && size !== 'xsmall' ? { alignSelf: 'center' } : {};

  return (
    <Box alignSelf="start" style={buttonAlignment}>
      {size !== 'small' && size !== 'xsmall' && item.tooltip ? (
        <Tip content={item.tooltip}>
          <Button
            icon={IconComponent}
            label={item.label}
            {...buttonProps}
          />
        </Tip>
      ) : (
        <Button
          icon={IconComponent}
          label={item.label}
          {...buttonProps}
        />
      )}
    </Box>
  );
};

// NavDropButton component to handle dropdown rendering logic
const NavDropButton = ({ item, activeDropdown, setActiveDropdown, children }) => {
  let lastMouseY = useRef(0); // To track the Y position of the mouse when leaving the button

  // Handle mouse enter on the combined wrapper (button and dropdown area)
  const handleMouseEnter = () => {
    setActiveDropdown(item.id); // Open dropdown when hovering over the entire combined area
  };

  // Handle mouse leave from the combined wrapper (button and dropdown area)
  const handleMouseLeave = (e) => {
    const relatedTarget = e.relatedTarget;

    // Ensure relatedTarget is valid (check if it's not null and is a DOM node)
    if (relatedTarget && relatedTarget instanceof Node) {
      if (e.currentTarget.contains(relatedTarget)) {
        return; // Don't close if the related target is inside the combined area
      }
    }

    // If mouse is leaving the parent button and moving south, we allow the dropdown to stay open
    const currentMouseY = e.clientY;

    if (currentMouseY > lastMouseY.current) {
      // If moving south, keep dropdown open
      return;
    }

    // Otherwise, close dropdown when leaving
    setActiveDropdown(null);
  };

  // Track the mouse position when moving over the button
  const handleMouseMove = (e) => {
    lastMouseY.current = e.clientY;
  };

  // Handle mouse enter on the dropdown itself
  const handleDropdownMouseEnter = () => {
    setActiveDropdown(item.id); // Ensure the dropdown remains open when inside
  };

  // Handle mouse leave from the dropdown
  const handleDropdownMouseLeave = () => {
    setActiveDropdown(null); // Close dropdown when mouse leaves the dropdown
  };

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove} // Track the mouse position
      direction="row"
      align="center"
      gap="small"
    >
      <DropButton
        key={item.id}
        label={item.label}
        reverse={true}
        icon={<FormDown />}
        dropAlign={{ top: 'bottom', left: 'left' }}
        open={activeDropdown === item.id}  // Only open if this dropdown is the active one
        onClose={() => setActiveDropdown(null)}  // Close when clicking outside
        dropContent={
          <Nav
            direction="column"
            gap="xxsmall"
            onMouseEnter={handleDropdownMouseEnter} // Mark dropdown as active
            onMouseLeave={handleDropdownMouseLeave}  // Close dropdown when mouse leaves the dropdown
          >
            {children}
          </Nav>
        }
        onClick={() => setActiveDropdown(activeDropdown === item.id ? null : item.id)} // Toggle dropdown on click
        pad={{ vertical: 'xsmall', horizontal: 'small' }}
      />
    </Box>
  );
};

// Core header component
const AppHeader = ({ toggleAgent }) => {
  const navigate = useNavigate();
  const size = useContext(ResponsiveContext);
  const { setHeaderHeight, headerRef } = useHeaderHeight();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // Track active dropdown

  const isActive = (path) => window.location.pathname === path;

  const handleItemClick = (item) => {
    if (item.id === 'aiagent') {
      toggleAgent();
    } else if (item.id === 'slack' || /^http/i.test(item.path)) {  
      window.open(item.path, '_blank');  
    } else {
      navigate(item.path); 
    }

    if (size === 'small' || size === 'xsmall') {
      setMenuOpen(false); // Close the menu on mobile when an item is clicked
    } else {
      setActiveDropdown(null);  // Close dropdown when a submenu item is clicked on desktop
    }
  };

  useEffect(() => {
    if (size !== 'small' && size !== 'xsmall') {
      setMenuOpen(false); 
    }
  }, [size]);

  // Capture any header height changes (used by AgentSidebar)
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeaderHeight();
    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [headerRef, setHeaderHeight]);

  return (
    <Header id="header" ref={headerRef} fill="horizontal" pad={{ horizontal: 'medium' }} background="background-front">
      <Tip content="Tech Con home...">
        <Button onClick={() => { navigate("/"); }} >
          <Box direction="row" align="center" gap="medium" pad={{ vertical: 'small' }} responsive={false}>
            <Box height="24px" justify="center" align="center">
              <HpeIcon color="brand" />
            </Box>
            <Box direction="row" gap="xsmall" align="center">
              <Text color="text-strong" weight="bold">HPE</Text>
              <Text color="text-strong" style={{ whiteSpace: 'nowrap' }}>Tech Con</Text>
            </Box>
          </Box>
        </Button>
      </Tip>

      {/* Desktop Nav */}
      {!['xsmall', 'small'].includes(size) ? (
        <Nav direction="row" gap="small" align="center">
          {MenuItems.map(item => (
            item.children ? (
              <NavDropButton
                key={item.id}
                item={item}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
                children={item.children.map(child => (
                  <NavButton 
                    key={`${item.id}-${child.id}`}  // Concatenate parent and child ids for uniqueness
                    item={child} 
                    onClick={handleItemClick} 
                    isActive={isActive} 
                    size={size} 
                  />
                ))} 
              />
            ) : (
              <NavButton 
                key={item.id} 
                item={item} 
                onClick={handleItemClick} 
                isActive={isActive} 
                size={size} 
              />
            )
          ))}
        </Nav>
      ) : (
        // Mobile Menu (No tooltips)
        <DropButton
          icon={<MenuIcon />}
          dropAlign={{ top: 'bottom', left: 'left' }}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          onOpen={() => setMenuOpen(true)}
          dropContent={
            <Nav direction="column" gap="xsmall" align="start">
              {MenuItems.map(item => (
                item.children ? (
                  <NavDropButton
                    key={item.id}
                    item={item}
                    activeDropdown={activeDropdown}
                    setActiveDropdown={setActiveDropdown}
                    children={item.children.map(child => (
                      <NavButton 
                        key={`${item.id}-${child.id}`}  // Concatenate parent and child ids for uniqueness
                        item={child} 
                        onClick={handleItemClick} 
                        isActive={isActive} 
                        size={size} 
                      />
                    ))} 
                  />
                ) : (
                  <NavButton 
                    key={item.id} 
                    item={item} 
                    onClick={handleItemClick} 
                    isActive={isActive} 
                    size={size} 
                  />
                )
              ))}
            </Nav>
          }
        />
      )}
    </Header>
  );
};

export { AppHeader as Header };

